/* eslint-disable @typescript-eslint/no-explicit-any */

import { AxiosResponse } from 'axios';
import { call, delay, put } from 'redux-saga/effects';

import api from '../../api/api';
import { errorHandling } from '../../api/errorHandling';
import { mapSuccessResponseToState } from '../../api/responseHandling';
import { getConfigStorageItem } from '../../hooks/useConfigStorage';
import i18n from '../../utils/i18n';
import { getApiVersion } from '../apiVersion';
import { PageNameType } from '../clientConfiguration/clientConfigurationSlice';
import reduxSlicesList from '../reduxSlicesList';

const buildTablePath = (table: string, version: number) => {
 const [base, ...rest] = table.split('/');
 const baseCap = base.charAt(0).toUpperCase() + base.slice(1);
 const sub = rest.length ? `/${rest.join('/')}` : '';
 return { path: `/${baseCap}/v${version}${sub}`, baseCap };
};

export function* getTable(props: any) {
 const slice = reduxSlicesList[props.payload.table as PageNameType];
 try {
  const { table, url, ...rest } = props.payload;
  const version = getApiVersion(table, 'getTable');
  const { path, baseCap } = buildTablePath(table, version);
  const response: AxiosResponse<any[]> = yield call(
   api.post,
   url || path,
   rest,
  );
  yield put(
   slice.getTableSuccess(
    mapSuccessResponseToState(response, baseCap, 'fetched'),
   ),
  );
  yield delay(0);
  yield put(slice.getTableFinish());
 } catch (error: unknown) {
  console.log('error', error);
  yield* errorHandling(error, slice.getTableFailure);
 }
}

export function* getItem(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, id } = props.payload;
  const version = getApiVersion(table, 'getItem');
  const { path, baseCap } = buildTablePath(table, version);
  const response: AxiosResponse<any[]> = yield call(api.get, `${path}/${id}`);

  yield put(
   slice.getItemSuccess(
    mapSuccessResponseToState(response, baseCap, 'fetched'),
   ),
  );
  yield delay(0);
  yield put(slice.getItemFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.getItemFailure);
 }
}

export function* createItem(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table } = props.payload;
  const version = getApiVersion(table, 'createItem');
  const { path } = buildTablePath(table, version);
  const response: AxiosResponse<any[]> = yield call(
   api.post,
   `${path}/create`,
   props.payload.data,
  );

  yield put(
   slice.createItemSuccess(
    mapSuccessResponseToState(response, 'Table', 'created'),
   ),
  );
  yield delay(0);
  yield put(slice.createItemFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.createItemFailure);
 }
}

export function* updateItem(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, id, data, isPut } = props.payload;
  const version = getApiVersion(table, 'updateItem');
  const { path, baseCap } = buildTablePath(table, version);

  const response: AxiosResponse<any[]> = yield call(
   isPut ? api.put : api.patch,
   `${path}/${isPut ? 'update' : 'patch'}/${id}`,
   data,
  );

  yield put(
   slice.updateItemSuccess(
    mapSuccessResponseToState(response, baseCap, 'updated'),
   ),
  );
  yield delay(0);
  yield put(slice.updateItemFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.updateItemFailure);
 }
}

export function* deleteItems(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, ids } = props.payload;
  const version = getApiVersion(table, 'deleteItems');
  const { path, baseCap } = buildTablePath(table, version);
  const response: AxiosResponse<any[]> = yield call(
   api.del,
   `${path}/abort`,
   ids,
  );

  yield put(
   slice.deleteItemsSuccess(
    mapSuccessResponseToState(response, baseCap, 'deleted'),
   ),
  );
  yield delay(0);
  yield put(slice.deleteItemsFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.deleteItemsFailure);
 }
}

export function* deleteItem(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, id } = props.payload;
  const version = getApiVersion(table, 'deleteItem');
  const { path, baseCap } = buildTablePath(table, version);
  const response: AxiosResponse<any[]> = yield call(
   api.del,
   `${path}/delete/${id}`,
   {},
  );

  yield put(
   slice.deleteItemSuccess(
    mapSuccessResponseToState(response, baseCap, 'deleted'),
   ),
  );
  yield delay(0);
  yield put(slice.deleteItemFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.deleteItemFailure);
 }
}

export function* getTableStructure(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table } = props.payload;
  const version = getApiVersion(table, 'getTableStructure');
  const { path, baseCap } = buildTablePath(table, version);
  const structureObj = getConfigStorageItem(baseCap)?.structure || [];
  if (structureObj.length === 0) {
   const response: AxiosResponse<any[]> = yield call(
    api.get,
    `${path}/structure?lang=${i18n.language ? i18n.language : 'lt'}`,
   );

   // it is saved to localStorage in tables page template
   yield put(
    slice.getTableStructureSuccess(
     mapSuccessResponseToState(response, `${baseCap} structure`, 'fetched'),
    ),
   );
   yield delay(0);
   yield put(slice.getTableStructureFinish());
  } else {
   yield put(
    slice.getTableStructureSuccess({
     data: structureObj,
    }),
   );
   yield delay(0);
   yield put(slice.getTableStructureFinish());
  }
 } catch (error: unknown) {
  yield* errorHandling(error, slice.getTableStructureFailure);
 }
}

export function* getItemDocuments(props: any) {
 const { table, id } = props.payload;
 const slice = reduxSlicesList[table as keyof typeof reduxSlicesList];
 const version = getApiVersion(table, 'getItemDocuments');
 const { path } = buildTablePath(table, version);

 try {
  const response: AxiosResponse<any[]> = yield call(
   api.get,
   `${path}/${id}/documents`,
  );

  yield put(
   slice.getItemDocumentsSuccess(
    mapSuccessResponseToState(response, 'Documents', 'fetched'),
   ),
  );
 } catch (error: unknown) {
  yield* errorHandling(error, slice.getItemDocumentsFailure);
 }
}

export function* uploadItemDocuments(props: any) {
 const { table, id, files } = props.payload;
 const slice = reduxSlicesList[table as keyof typeof reduxSlicesList];
 const version = getApiVersion(table, 'uploadItemDocuments');
 const { path } = buildTablePath(table, version);

 try {
  const formData = new FormData();
  Array.from(files).forEach((f: any, i: number) => {
   formData.append(`uploads[${i}].File`, f);
   formData.append(`uploads[${i}].Name`, f.name || 'noname');
  });

  const response: AxiosResponse<any[]> = yield call(
   api.post,
   `${path}/${id}/documents/upload`,
   formData,
  );

  yield put(
   slice.uploadItemDocumentsSuccess(
    mapSuccessResponseToState(response, 'Documents', 'uploaded'),
   ),
  );
  yield delay(0);
  yield put(slice.uploadItemDocumentsFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.uploadItemDocumentsFailure);
 }
}

export function* deleteItemDocument(props: any) {
 const { table, id, documentId } = props.payload;
 const slice = reduxSlicesList[table as keyof typeof reduxSlicesList];
 const version = getApiVersion(table, 'deleteItemDocument');
 const { path } = buildTablePath(table, version);
 try {
  const response: AxiosResponse<any[]> = yield call(
   api.del,
   `${path}/${id}/documents/delete/${documentId}`,
   {},
  );

  yield put(
   slice.deleteItemDocumentSuccess(
    mapSuccessResponseToState(response, 'Document', 'deleted'),
   ),
  );
  yield delay(0);
  yield put(slice.deleteItemDocumentFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.deleteItemDocumentFailure);
 }
}

export function* getTableExcel(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, ...rest } = props.payload;
  const version = getApiVersion(table, 'getTableExcel');
  const { path, baseCap } = buildTablePath(table, version);
  const response: AxiosResponse<any> = yield call(
   api.post,
   `${path}/excel`,
   { ...rest, lang: i18n.language ? i18n.language : 'lt', limit: 10000 },
  );
  const { data, ...restResponse } = mapSuccessResponseToState(
   response,
   baseCap,
   'fetched',
  );
  yield put(
   slice.getTableExcelSuccess({
    ...restResponse,
    excel: {
     name: data.Name,
     type: data.Type,
     file: data.File,
     extension: data.Extension,
    },
   }),
  );
  yield delay(0);
  yield put(slice.getTableExcelFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.getTableExcelFailure);
 }
}
